import {
  getLegacyThemeUrls,
  getExtraSoundUrls,
  buildSoundsFromSoundAssets,
} from 'chessboard/plugins/sounds/generate-sound-urls';
import { Sounds } from 'chessboard/plugins/sounds/config/types';
import { SoundUtil } from '@chesscom/web-ui';
import type { ThemeAssets } from 'chessboard/config/theme-assets';

const { loadSound, playSound, enableSound } = SoundUtil;

export const boardCodes: Sounds.BoardSounds[] = [
  Sounds.BoardSounds.GameStart,
  Sounds.BoardSounds.GameEnd,
  Sounds.BoardSounds.Capture,
  Sounds.BoardSounds.Castle,
  Sounds.BoardSounds.Premove,
  Sounds.BoardSounds.MoveSelf,
  Sounds.BoardSounds.MoveCheck,
  Sounds.BoardSounds.MoveOpponent,
  Sounds.BoardSounds.Promote,
  Sounds.BoardSounds.Notify,
  Sounds.BoardSounds.TenSeconds,
  Sounds.BoardSounds.Illegal,
];

/**
 * Sounds injects HTMLAudioElements into the page and accesses their play method directly.
 */
export function createSounds(params: Sounds.CreateParams): Sounds.Model {
  const options = params.options;
  let codes: Sounds.BoardSounds[] | undefined = boardCodes;
  const theme = options?.soundTheme || 'default';

  if (params) {
    codes = params.codes;
  }

  let sounds: Sounds.SoundObject;
  let soundTheme = theme;

  load({ codes, theme, soundAssets: params?.soundAssets });

  return {
    destroy,
    getSounds,
    getTheme,
    load,
    loadExtra,
    play,
  };

  /**
   * Clean up by removing the audio elements.
   */
  function destroy(): void {
    Object.keys(sounds).forEach((sound) => {
      sounds[sound].unload();
    });
  }

  /**
   * Get an object of the HTMLAudioElements.
   */
  function getSounds(): Sounds.SoundObject {
    return sounds;
  }

  /**
   * Get the theme.
   */
  function getTheme() {
    return soundTheme;
  }

  /**
   * Load a theme of sounds into the page, with optional codes. Defaults to using
   * the 'default' theme with the standard board codes.
   */
  function load(soundParams: {
    theme: string;
    codes?: string[];
    soundAssets?: ThemeAssets.SoundAssets;
  }): void {
    soundTheme = soundParams.theme;

    if (soundParams.soundAssets) {
      sounds = buildSoundsFromSoundAssets(soundParams.soundAssets);
    } else {
      sounds = (soundParams.codes || boardCodes).reduce(
        (acc, soundType) => {
          acc[soundType] = loadSound(
            getLegacyThemeUrls(soundTheme, soundType, options.assetPaths?.soundsTheme),
          );
          return acc;
        },
        <Sounds.SoundObject>{},
      );
    }

    enableSound();
  }

  /**
   * Load extra sounds.
   */
  function loadExtra(soundParams: { baseURL: string; codes: string[] }): void {
    const extraSounds = soundParams.codes.reduce(
      (acc, soundType) => {
        acc[soundType] = loadSound(getExtraSoundUrls(soundParams.baseURL, soundType));
        return acc;
      },
      <Sounds.SoundObject>{},
    );

    Object.keys(extraSounds).forEach((key) => {
      sounds[key]?.unload();

      sounds[key] = extraSounds[key];
    });

    enableSound();
  }

  /**
   * Play a sound.
   */
  function play(code: string): string {
    const sound = sounds[code];

    const currTheme = getTheme(); // return current theme for cypress tests

    if (!sound) {
      return currTheme;
    }

    try {
      playSound(sound);
      return currTheme;
    } catch (e) {
      // Don't stop execution if sound can't be played
      return currTheme;
    }
  }
}
